/**
 * What a person in the room offers: speak in the room (your own character), send your agent a
 * prompt through remote control (your own character — nobody else's agents take instructions from
 * you), and look at their day.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, MessageCircle, Send, Shirt, TerminalSquare, X } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { chatErrorKey } from '../bughunt/useRoomChat'
import { ConfirmIdentity } from './ConfirmIdentity'
import { useFloating } from './useFloating'

export function PersonMenu({
  name,
  developerId,
  isMe,
  x,
  y,
  onSay,
  onPrompt,
  onCharacter,
  onClose,
  children,
}: {
  name: string
  developerId: string
  isMe: boolean
  x: number
  y: number
  onSay: () => void
  onPrompt: () => void
  /** Your own character: change how it looks, right there (docs/refactor/25). */
  onCharacter?: () => void
  onClose: () => void
  /** More for one's own character — "Ir para…" an office area (docs/refactor/29). */
  children?: ReactNode
}) {
  const { t } = useI18n()
  const { ref, position } = useFloating(x, y, onClose)

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button:not(:disabled), a')?.focus()
  }, [ref])

  return (
    <div
      ref={ref}
      className="bug-menu person-menu"
      role="menu"
      aria-label={name}
      style={{ left: position.left, top: position.top }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="bug-menu__head">
        <span className="bold small truncate">{name}</span>
        {isMe && <span className="tiny muted">{t('room.menu.you')}</span>}
      </div>

      <button type="button" role="menuitem" className="bug-menu__item" onClick={onSay} disabled={!isMe} title={isMe ? undefined : t('room.menu.sayOnlyYou')}>
        <MessageCircle size={14} aria-hidden /> {t('room.menu.say')}
      </button>

      <button type="button" role="menuitem" className="bug-menu__item" onClick={onPrompt} disabled={!isMe} title={isMe ? undefined : t('room.menu.promptOnlyOwner')}>
        <TerminalSquare size={14} aria-hidden /> {t('room.menu.prompt')}
      </button>
      {!isMe && <p className="person-menu__why tiny muted">{t('room.menu.promptOnlyOwner')}</p>}

      {isMe && onCharacter && (
        <button type="button" role="menuitem" className="bug-menu__item" onClick={onCharacter}>
          <Shirt size={14} aria-hidden /> {t('room.menu.character')}
        </button>
      )}

      {children}

      <div className="bug-menu__sep" />

      <Link to={isMe ? '/track/day' : `/developers/${developerId}/day`} role="menuitem" className="bug-menu__item" onClick={onClose}>
        <CalendarDays size={14} aria-hidden /> {t('room.menu.day')}
      </Link>
    </div>
  )
}

/** A line to say in the room, typed where the character stands. Enter says it. */
export function SayBox({ x, y, onSay, onClose }: { x: number; y: number; onSay: (text: string) => Promise<unknown>; onClose: () => void }) {
  const { t } = useI18n()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { ref, position } = useFloating(x, y, onClose, [error])

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    const line = text.trim()
    if (!line || busy) return
    setBusy(true)
    setError(null)
    try {
      await onSay(line)
      onClose()
    } catch (failure) {
      setError(t(chatErrorKey(failure)))
      setBusy(false)
    }
  }

  return (
    <form ref={ref} className="say-box" style={{ left: position.left, top: position.top }} onSubmit={(event) => void submit(event)}>
      <MessageCircle size={14} aria-hidden className="say-box__icon" />
      <input
        className="input"
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={280}
        placeholder={t('room.say.placeholder')}
        aria-label={t('room.say.label')}
        autoFocus
        disabled={busy}
      />
      <button type="submit" className="btn btn--sm btn--primary btn--icon" disabled={!text.trim() || busy} aria-label={t('room.say.send')}>
        <Send size={13} />
      </button>
      {error && <span className="say-box__error tiny">{error}</span>}
    </form>
  )
}

/**
 * A prompt for your own agent, through remote control: the next of your agents to finish its turn
 * takes it. When remote control is off, one click turns it on for two hours and sends.
 */
export function PromptBox({ x, y, onClose, onSent }: { x: number; y: number; onClose: () => void; onSent: () => void }) {
  const { t } = useI18n()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ message: string; canSwitchOn: boolean } | null>(null)
  // Switching remote control on asks for a recent sign-in; the form appears here when it does.
  const [confirming, setConfirming] = useState(false)
  const { ref, position } = useFloating(x, y, onClose, [error, confirming])

  const send = async (switchOn: boolean): Promise<void> => {
    const prompt = text.trim()
    if (!prompt || busy) return
    setBusy(true)
    setError(null)
    try {
      if (switchOn) await api.remote.setEnabled(true, 2)
      await api.remote.send(prompt, null)
      onSent()
      onClose()
    } catch (failure) {
      const code = failure instanceof ApiError ? failure.code : undefined
      if (code === 'reauth_required') {
        setConfirming(true)
        setError(null)
        setBusy(false)
        return
      }
      setError({
        message:
          code === 'remote_off'
            ? t('room.prompt.off')
            : code === 'remote_not_allowed'
              ? t('remote.error.remote_not_allowed')
              : code === 'not_a_developer' || (failure instanceof ApiError && failure.status === 403)
                ? t('remote.noDeveloper')
                : code && ['too_many_pending', 'too_long', 'required'].includes(code)
                  ? t(`remote.error.${code}`)
                  : t('error.generic'),
        canSwitchOn: code === 'remote_off',
      })
      setBusy(false)
    }
  }

  return (
    <form
      ref={ref}
      className="prompt-box"
      style={{ left: position.left, top: position.top }}
      onSubmit={(event) => {
        event.preventDefault()
        void send(false)
      }}
    >
      <header className="row row--tight">
        <TerminalSquare size={14} aria-hidden />
        <span className="small bold">{t('room.prompt.title')}</span>
        <div className="spacer" />
        <button type="button" className="btn btn--sm btn--ghost btn--icon" onClick={onClose} aria-label={t('common.close')}>
          <X size={13} />
        </button>
      </header>
      <textarea
        className="textarea"
        rows={4}
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={4000}
        placeholder={t('room.prompt.placeholder')}
        aria-label={t('room.prompt.title')}
        autoFocus
        disabled={busy}
        onKeyDown={(event) => {
          // Enter sends; Ctrl+Enter (and Shift+Enter) breaks the line (the owner's call, 16/10).
          if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') return
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            void send(false)
          }
        }}
      />
      <span className="tiny muted">{t('room.prompt.hint')}</span>
      {confirming && (
        <ConfirmIdentity
          onConfirmed={() => {
            setConfirming(false)
            void send(true)
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
      {error && (
        <div className="prompt-box__error small" role="alert">
          <span>{error.message}</span>
          {error.canSwitchOn && (
            <button type="button" className="btn btn--sm btn--primary" onClick={() => void send(true)} disabled={busy}>
              {t('room.prompt.switchOnAndSend')}
            </button>
          )}
        </div>
      )}
      <div className="row row--tight" style={{ justifyContent: 'flex-end' }}>
        <Link to="/agents/remote" className="btn btn--sm btn--ghost" onClick={onClose}>
          {t('room.prompt.openRemote')}
        </Link>
        <button type="submit" className="btn btn--sm btn--primary" disabled={!text.trim() || busy}>
          <Send size={13} aria-hidden /> {t('remote.send')}
        </button>
      </div>
    </form>
  )
}

/** A short note in the corner of the room, gone by itself. */
export function RoomNote({ children }: { children: ReactNode }) {
  return (
    <div className="bug-note room-note" role="status">
      {children}
    </div>
  )
}
